import React, { useState, Suspense, useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Component imports
import LoginForm from "./components/LoginForm";
import CustomerCard from "./components/CustomerCard";
import ArticleList from "./components/ArticleList";
import ReturnList from "./components/ReturnList";
import Confirmation from "./components/Confirmation";
import Loader from "./components/Loader";
import GeneralReturnForm from "./components/GeneralReturnForm";

const returnAPIBaseUrl = process.env.REACT_APP_API_BASE_URL;
const SESSION_SITE_KEY = "site";
const INPOST_SPL_RETURN_URL = "https://szybkiezwroty.pl/pl/GUSTI_LEDER";

const redirectToExternalReturnPortal = (url) => {
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch (error) {
    console.log("Unable to redirect the parent frame", error);
  }

  window.location.href = url;
};

const getSessionSite = () => {
  try {
    const siteParam = new URLSearchParams(window.location.search).get("site");
    if (siteParam) {
      const site = siteParam.toLowerCase();
      sessionStorage.setItem(SESSION_SITE_KEY, site);
      return site;
    }
  } catch (error) {
    console.log("Unable to resolve site parameter", error);
  }

  return sessionStorage.getItem(SESSION_SITE_KEY);
};

const clearSessionStorageExceptSite = () => {
  const site = sessionStorage.getItem(SESSION_SITE_KEY);
  sessionStorage.clear();

  if (site) {
    sessionStorage.setItem(SESSION_SITE_KEY, site);
  }
};

function App() {
  // State management
  const [userData, setUserData] = useState(
    JSON.parse(sessionStorage.getItem("user"))
  );
  const [orderData, setOrderData] = useState(
    JSON.parse(sessionStorage.getItem("orderData"))
  );
  const [articleList, setArticleList] = useState(
    JSON.parse(sessionStorage.getItem("articleList"))
  );
  const [missingReasonList, setMissingReasonList] = useState(null);
  const [returnList, setReturnList] = useState(
    JSON.parse(sessionStorage.getItem("returnList"))
  );
  const [ups, setUps] = useState(false);
  const [dhl, setDhl] = useState(false);
  const [dhlToUpsReturn, setDhlToUpsReturn] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [commentPosted, setCommentPosted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userComment, setUserComment] = useState("");
  const [labelSupport, setLabelSupport] = useState(false);
  const [supportError, setSupportError] = useState(false);
  const [dhlLabelImageUrl, setDhlLabelImageUrl] = useState(null);
  const { i18n } = useTranslation();
  const [joLuSite] = useState(() => getSessionSite() === "jolu");

  useEffect(() => {
    if (joLuSite) {
      document.body.classList.add("site-jolu");
    } else {
      document.body.classList.remove("site-jolu");
    }

    return () => {
      document.body.classList.remove("site-jolu");
    };
  }, [joLuSite]);

  const currentLang = i18n.language;
  console.log('currentLang:',currentLang);

  // Page navigation logic
  let navigate = useNavigate();

  // Fetching data logic

  const getOrderData = () => {
    axios
      .get(`${returnAPIBaseUrl}/articles`)
      .then((response) => {
        const orderData = response.data;

        if (
          orderData.advertisingMediumCode === "SPL" &&
          orderData.carrier === "INPOST"
        ) {
          redirectToExternalReturnPortal(INPOST_SPL_RETURN_URL);
          return;
        }

        sessionStorage.setItem("orderData", JSON.stringify(orderData));
        setOrderData(JSON.parse(sessionStorage.getItem("orderData")));

        const articleList = orderData.articleList;
        const articleListWithCheckedProperty = articleList.map((item) => {
          return { ...item, checked: false };
        });
        sessionStorage.setItem(
          "articleList",
          JSON.stringify(articleListWithCheckedProperty)
        );
        setArticleList(JSON.parse(sessionStorage.getItem("articleList")));
      })
      .catch((error) => {
        console.log(error);
        // set any kind of error state here
      });
  };

  const createReturnDataObject = () => {
    const relevantArticleData = [];
    returnList.forEach((article) => {
      const relevantData = {
        articleNumber: article.articleNumber,
        reason: article.returnReason || null,
        quantity: article.returnQuantity,
      };
      relevantArticleData.push(relevantData);
    });
    const returnData = {
      orderNumber: orderData.orderNumber,
      customerEmail: orderData.customer.email,
      customerEmailLanguage: currentLang,
      advertisingMediumCode: orderData.advertisingMediumCode,
      articles: relevantArticleData,
      userComment: userComment,
    };

    return returnData;
  };

  const postOrderComment = async () => {
    // only need to pass relevant article data to backend/ customer service like return rason etc.
    const requestBodyData = createReturnDataObject();

    setIsLoading(true);
    const response = await axios.post(`${returnAPIBaseUrl}/postReturn`, {
      data: requestBodyData,
    });

    setIsLoading(false);
    return response;
  };

  const postReturnTrackingNumber = async (returnTrackingNumber) => {
    const requestBodyData = {
      orderNumber: orderData.orderNumber,
      trackingNumber: returnTrackingNumber,
      shippingRequestNumber: orderData.shippingRequestNumber,
      packageNumber: orderData.packageId
    };
    setIsLoading(true);
    const response = axios.post(`${returnAPIBaseUrl}/postReturn`, {
      data: requestBodyData,
    });
    setIsLoading(false);
    return response;
  };

  const requestDhlLabel = async () => {
    const DHL_URL = `${returnAPIBaseUrl}/dhlLabel`;
    const requestBodyData = {
      orderData: orderData,
      returnList: returnList,
      customerEmailLanguage: currentLang,
      userComment: userComment,
    };
    setIsLoading(true);
    try {
      const response = await axios.post(DHL_URL, { data: requestBodyData });
      console.log("response from requestDhlLabel() post");
      console.log(response);
      setIsLoading(false);
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const sendMailToSupport = async (customerCountryCode, reason) => {
    if (reason === undefined) {
      reason = "Customer needs support with label";
    }
    const SUPPORT_URL = `${returnAPIBaseUrl}/labelSupport`;
    const returnData = createReturnDataObject();

    const requestBodyData = {
      returnData: returnData,
      reason: reason,
      countryCode: customerCountryCode,
      orderData: orderData,
    };
    setIsLoading(true);
    const response = await axios.post(SUPPORT_URL, { data: requestBodyData });
    setIsLoading(false);
    return response;
  };

  // Click handler logic

  const onLoggedIn = () => {
    setUserData(JSON.parse(sessionStorage.getItem("user")));

    getOrderData();
  };

  const toggleCheck = (index) => {
    let mapped = articleList.map((articleInfo, i) => {
      // return articleInfo.articleNumber === articleNumber ? { ...articleInfo, checked: !articleInfo.checked } : { ...articleInfo };
      if (i !== index) {
        return { ...articleInfo };
      }

      if (articleInfo.checked) {
        return {
          ...articleInfo,
          checked: false,
          returnReason: null,
          returnQuantity: null,
        };
      }

      return {
        ...articleInfo,
        checked: true,
        returnQuantity: articleInfo.returnQuantity || 1,
      };
    });
    sessionStorage.setItem("articleList", JSON.stringify(mapped));
    setArticleList(JSON.parse(sessionStorage.getItem("articleList")));
  };

  const handleReturnReasonSelect = (event, index) => {
    const returnReason = parseInt(event.target.value);
    const mapped = articleList.map((articleInfo, i) => {
      //return articleInfo.articleNumber === article.articleNumber ? { ...articleInfo, returnReason: returnReason } : { ...articleInfo };
      return i === index
        ? {
            ...articleInfo,
            returnReason: returnReason > 0 ? returnReason : null,
            returnQuantity: articleInfo.returnQuantity || 1,
          }
        : { ...articleInfo };
    });

    sessionStorage.setItem("articleList", JSON.stringify(mapped));
    setArticleList(JSON.parse(sessionStorage.getItem("articleList")));
  };

  const handleQuantitySelect = (event, index) => {
    const quantity = parseInt(event.target.value) || 1; // if there is only 1 article, no value will be passed (bc no dropdown is present)
    if (quantity === 0) {
      console.log("no quantity selected");
    } else {
      let mapped = articleList.map((articleInfo, i) => {
        return i === index
          ? { ...articleInfo, returnQuantity: quantity }
          : { ...articleInfo };
      });
      sessionStorage.setItem("articleList", JSON.stringify(mapped));
      setArticleList(JSON.parse(sessionStorage.getItem("articleList")));
    }
  };

  const handleUserCommentChange = (event) => {
    event.preventDefault();
    const text = event.target.value;
    // remove html tags if existing - for security reasons - there is additional sanitizing in backend
    const regex = /(<([^>]+)>)/gi;
    const cleanText = text.replace(regex, "");
    setUserComment(cleanText);
  };

  const handleListSubmit = () => {
    const checkedList = articleList.filter((article) => article.checked);
    setMissingReasonList([]);
    sessionStorage.setItem("returnList", JSON.stringify(checkedList));
    setReturnList(JSON.parse(sessionStorage.getItem("returnList")));
    navigate("returns");
  };

  const handleLabelRequest = async () => {
    navigate("confirmation");
    const originalCarrier = orderData.carrier;

    const customerCountryCode = orderData.customer.address.countryCode;

    const supportedCountries = [
      "AT", "DE", "NL", "SE", "IT", "FR", "FI", "PT", "BE", "SK", "CZ", "HU", "GR", "RO", "BG", "LU", "DK", "IE", "CY", "HR", "ES"
    ];

    setIsLoading(true);
    const response = await postOrderComment();
    if (response.status === 200) {
      setCommentPosted(true);

      if (supportedCountries.includes(customerCountryCode)) {

        console.log("case DHL + " + customerCountryCode);
        

        const response = await requestDhlLabel();
        
        console.log(response);

        if (response) {
          setEmailSent(true);
          setDhl(true);
          const returnTrackingNumber = response.data.trackingNumber; // make sure you return this from customUpsLabel endpoint
          postReturnTrackingNumber(returnTrackingNumber);
          setIsLoading(false);

          const dhlLabelBase64 = response.data.labelBase64;
          if (dhlLabelBase64) {
            const base64Url = `data:image/png;base64,${dhlLabelBase64}`;
            const response = await fetch(base64Url);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setDhlLabelImageUrl(url);
          }
        } else {
          setLabelSupport(true);
          let reason = `Original Carrier: ${originalCarrier}. Custom DHL return label could not be generated properly. Please handle manually`;
          const response = await sendMailToSupport(customerCountryCode, reason);
          if (response.status === 200) {
            setIsLoading(false);
          } else {
            setLabelSupport(false);
            setSupportError(true);
            setIsLoading(false);
          }
        }
      } else {
        setLabelSupport(true);
        let reason = `Original Carrier: ${originalCarrier} <br>Customer Country: ${customerCountryCode}<br> Return label could not be generated. Please handle manually`;
        const response = await sendMailToSupport(customerCountryCode, reason);
        if (response.status === 200) {
          setIsLoading(false);
        } else {
          setLabelSupport(false);
          setSupportError(true);
          setIsLoading(false);
        }
      }
    } else {
      setSupportError(true);
      setIsLoading(false);
    }
  };

  const backToArticleList = () => {
    const unchecked = articleList.map((article) => {
      return { ...article, checked: false, returnReason: null };
    });

    sessionStorage.removeItem("returnList");
    sessionStorage.setItem("articleList", JSON.stringify(unchecked));
    setArticleList(JSON.parse(sessionStorage.getItem("articleList")));
    setMissingReasonList([]);
    navigate("/");
  };

  const cancelReturnProcess = () => {
    localStorage.clear();
    clearSessionStorageExceptSite();
    setUserData(null);
    setOrderData(null);
    setArticleList(null);
    setReturnList(null);
    setUserComment("");
    setMissingReasonList(null);
    setUps(false);
    setDhl(false);
    setDhlToUpsReturn(false);
    setLabelSupport(false);
    setSupportError(false);
    setEmailSent(false);
    setCommentPosted(false);
    setIsLoading(false);
    setMissingReasonList([]);

    navigate("/");
  };

  const handleCancelClick = () => {
    clearSessionStorageExceptSite();
    localStorage.clear();
    window.location.reload();
  };

  const getDaysDifference = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
  
    if (isNaN(d1) || isNaN(d2)) return null; // Handle invalid dates
  
    // Convert both dates to midnight to ignore time differences
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
  
    const timeDiff = Math.abs(d2 - d1); // Get difference in milliseconds
    return Math.floor(timeDiff / (1000 * 60 * 60 * 24)); // Convert to days
  };

  if (!userData) {
    return (
      <div className="main-container">
        <Routes>
          <Route
            path="/"
            element={
              <LoginForm
                onLoggedIn={onLoggedIn}
                returnAPIBaseUrl={returnAPIBaseUrl}
              />
            }
          />
          <Route
            path="/general-return"
            element={
              <GeneralReturnForm
                returnAPIBaseUrl={returnAPIBaseUrl}
                customerEmailLanguage={currentLang}
                isJoLuOrder={joLuSite}
              />
            }
          />
        </Routes>
      </div>
    );
  } else {
    return (
      <div className="main-container">
        {userData && orderData && (
          <CustomerCard
            firstName={orderData.customer.firstName}
            lastName={orderData.customer.lastName}
            orderNumber={orderData.orderNumber}
            email={orderData.customer.email}
            supportLogin={userData.supportLogin}
          />
        )}
        {articleList && (
          <Routes>
            <Route
              path="/"
              element={
                <ArticleList
                  articleList={articleList}
                  handleCancelClick={handleCancelClick}
                  toggleCheck={toggleCheck}
                  handleReturnReasonSelect={handleReturnReasonSelect}
                  handleQuantitySelect={handleQuantitySelect}
                  handleListSubmit={handleListSubmit}
                  missingReasonList={missingReasonList}
                />
              }
            />
            <Route
              path="/returns"
              element={
                <ReturnList
                  returnList={returnList}
                  orderData={orderData}
                  backToArticleList={backToArticleList}
                  cancelReturnProcess={cancelReturnProcess}
                  handleUserCommentChange={handleUserCommentChange}
                  userComment={userComment}
                  handleLabelRequest={handleLabelRequest}
                />
              }
            />
            <Route
              path="/confirmation"
              element={
                <Confirmation
                  ups={ups}
                  dhl={dhl}
                  dhlToUps={dhlToUpsReturn}
                  email={orderData.customer.email}
                  labelSupport={labelSupport}
                  supportError={supportError}
                  isLoading={isLoading}
                  terminate={cancelReturnProcess}
                  emailSent={emailSent}
                  commentPosted={commentPosted}
                  dhlLabelImageUrl={dhlLabelImageUrl}
                />
              }
            />
          </Routes>
        )}
      </div>
    );
  }
}

export default function WrappedApp() {
  return (
    <Suspense fallback={<Loader />}>
      <App />
    </Suspense>
  );
}
